import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type Boost } from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, StatTile, type Column } from '@/components/ui';
import { IconAward } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

/** Driver Boosts — the zone/time-window earnings bonuses incentives-svc serves to
 *  the driver app's Opportunities screen ("Boosts" section). A boost pays a flat
 *  $amount for trips completed in a named zone during a time window (optionally
 *  constrained to one weekday). incentives-svc OWNS the `driver_boosts` table; the
 *  rider/driver apps read the active ones via incentives-svc.
 *
 *  READ-ONLY here — by design, for now. incentives-svc exposes only the
 *  driver-facing read + opt-in/opt-out; it has NO internal admin endpoint to
 *  create / edit / delete a boost, so there is nothing for admin-svc to proxy a
 *  write to (and admin-svc never writes a table another service owns). This is a
 *  clean list + detail VIEW (no dead write buttons). Full CRUD lights up the day
 *  incentives-svc grows the authoring endpoints — see the read-only banner. The
 *  section is DEFENSIVE (shows a "not yet available" state when the table isn't in
 *  this Postgres). Covers the legacy Filament DriverBoostResource (its columns +
 *  the Active/Inactive status badge). */
export function Boosts() {
  const [q, setQ] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Boost | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'boosts', q, activeOnly, offset],
    queryFn: ({ signal }) => adminApi.boosts({ q, active: activeOnly ? 1 : undefined, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  // Stats roll-up (count / active / top bonus / opt-ins) — its own light query so
  // the tiles render even while the table page is refetching.
  const stats = useQuery({
    queryKey: ['admin', 'boosts', 'stats'],
    queryFn: ({ signal }) => adminApi.boostStats(signal),
  });

  const unavailable = data && data.available === false;

  const columns: Array<Column<Boost>> = [
    { key: 'id', header: 'ID', cell: (r) => <span className="metric text-xs text-muted">#{r.id}</span> },
    {
      key: 'zone',
      header: 'Zone',
      cell: (r) =>
        r.zone_name ? (
          <span className="font-medium text-slate-100">{r.zone_name}</span>
        ) : (
          <span className="text-xs text-muted">All zones</span>
        ),
    },
    {
      key: 'amount',
      header: 'Bonus',
      align: 'right',
      cell: (r) => <span className="metric text-sm text-ok">+${r.amount.toFixed(2)}</span>,
    },
    {
      key: 'window',
      header: 'Time window',
      cell: (r) => (
        <span className="metric text-xs text-slate-300">
          {hm(r.time_window_start)} – {hm(r.time_window_end)}
        </span>
      ),
    },
    {
      key: 'day',
      header: 'Day',
      cell: (r) => <span className="text-xs text-slate-300">{r.day_of_week_name}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (r) => (
        <Badge tone={r.status === 1 ? 'ok' : 'neutral'} dot>
          {r.status === 1 ? 'active' : 'inactive'}
        </Badge>
      ),
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <AdminPage>
      <AdminPageHeader
        title="Boosts"
        subtitle="Zone + time-window earnings bonuses drivers see on their Opportunities screen."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by zone…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Boosts not yet in Postgres"
            description={
              data?.note ??
              'The driver_boosts table is owned by incentives-svc and is not in this Postgres yet.'
            }
            icon={<IconAward className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {/* Stat tiles — glance figures over the boost set. */}
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Total boosts" value={stats.data?.stats?.total ?? '—'} icon={<IconAward className="h-4 w-4" />} />
            <StatTile label="Active" value={stats.data?.stats?.active ?? '—'} tone="ok" />
            <StatTile
              label="Top active bonus"
              value={stats.data?.stats ? `$${stats.data.stats.max_amount.toFixed(2)}` : '—'}
              tone="info"
            />
            <StatTile label="Driver opt-ins" value={stats.data?.stats?.optins_total ?? '—'} hint="across all boosts" />
          </div>

          {/* Read-only notice — this surface is intentionally a view (no authoring
              endpoint exists upstream yet). Keeps the page honest: no dead buttons. */}
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-ink-800 bg-ink-850 px-3 py-2 text-xs text-text-secondary">
            <IconAward className="mt-0.5 h-4 w-4 shrink-0 text-brand-400" />
            <span>
              Read-only view. Boosts are authored upstream in incentives-svc; create / edit / delete will appear here once
              incentives-svc exposes its internal boost-authoring endpoints.
            </span>
          </div>

          <div className="mb-3 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => {
                  setActiveOnly(e.target.checked);
                  setOffset(0);
                }}
                className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500"
              />
              Active only
            </label>
          </div>

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading boosts…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load boosts" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<Boost>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No boosts"
                      description={
                        activeOnly || q
                          ? 'No driver boosts match this filter.'
                          : 'No driver boosts configured yet.'
                      }
                      icon={<IconAward className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected !== null ? <BoostDrawer boost={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

// ─── boost detail drawer (read-only) ─────────────────────────

/** BoostDrawer — the read-only detail panel for one boost. Seeded directly from
 *  the row (no extra fetch needed — the list already carries the full row), so it
 *  opens instantly. Purely informational, matching the read-only surface. */
function BoostDrawer({ boost, onClose }: { boost: Boost; onClose: () => void }) {
  return (
    <Drawer
      title={`Boost #${boost.id}`}
      subtitle={boost.zone_name || 'All zones'}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <span className="metric text-2xl font-semibold text-ok">+${boost.amount.toFixed(2)}</span>
          <Badge tone={boost.status === 1 ? 'ok' : 'neutral'} dot>
            {boost.status === 1 ? 'active' : 'inactive'}
          </Badge>
        </div>

        <Card>
          <div>
            <Field label="ID" value={`#${boost.id}`} />
            <Field label="Zone" value={boost.zone_name || 'All zones'} />
            <Field label="Bonus" value={`$${boost.amount.toFixed(2)}`} />
            <Field label="Time window" value={`${hm(boost.time_window_start)} – ${hm(boost.time_window_end)}`} />
            <Field label="Day" value={boost.day_of_week_name} />
            <Field label="Status" value={boost.status === 1 ? 'Active' : 'Inactive'} />
          </div>
        </Card>

        <p className="text-[11px] text-text-muted">
          Read-only. This boost is authored in incentives-svc; it surfaces on the driver app's Opportunities screen while
          active.
        </p>
      </div>
    </Drawer>
  );
}

/** Trim a "HH:MM:SS" time string from Postgres to "HH:MM" for display. Passes a
 *  short/empty value through unchanged so a malformed row is still visible. */
function hm(t: string | null | undefined): string {
  if (!t) return '—';
  return t.length >= 5 ? t.slice(0, 5) : t;
}
